import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CalendarMonth as CalendarIcon,
  SsidChart as BehaviorInfoIcon,
  GraphicEq as BasicInfoIcon,
  AccessAlarm as TimeIcon,
  CrisisAlert as FirstSeizureIcon,
  VideoLibrary as VideoIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  ChevronRight as ChevronRightIcon,
  Notes,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import NoEntries from '../components/NoEntries';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CardSkeleton } from '../components/SkeletonLoader';
import ErrorState from '../components/ErrorState';
import AppStyles from '../styles/AppStyles';
import { rebuildSingleUrlWithCurrentToken } from '../utils/fileUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function Seizures() {
  const { t } = useTranslation();
  const [petId, setPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const [selectedTab, setSelectedTab] = useState(0);
  const [trendStart, setTrendStart] = useState(0); // pagination start index for monthly trends (6-month window)
  const [selectedSeizure, setSelectedSeizure] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const queryClient = useQueryClient();

  const seizuresQuery = useQuery({
    queryKey: ['seizures', petId],
    enabled: !!petId,
    queryFn: async () => {
      const token = localStorage.getItem('vetToken');
      const currentPetId = localStorage.getItem('currentPetId') || petId;
      if (!currentPetId) return [];
      const resp = await fetch(`${API_BASE}/api/sections-seizures?petId=${encodeURIComponent(currentPetId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('seizures.failedToLoad'));
      return Array.isArray(data) ? data : [];
    }
  });

  useEffect(() => {
    // Keep local petId in sync and refresh cache on change
    const currentPetId = localStorage.getItem('currentPetId') || petId;
    if (currentPetId !== petId) setPetId(currentPetId || '');
    if (currentPetId) {
      queryClient.invalidateQueries({ queryKey: ['seizures', currentPetId] });
    }
  }, [petId, queryClient]);

  useEffect(() => {
    const handlePetChange = () => {
      const currentPetId = localStorage.getItem('currentPetId');
      if (currentPetId && currentPetId !== petId) {
        setPetId(currentPetId);
      }
    };
    window.addEventListener('storage', handlePetChange);
    window.addEventListener('petChanged', handlePetChange);
    return () => {
      window.removeEventListener('storage', handlePetChange);
      window.removeEventListener('petChanged', handlePetChange);
    };
  }, [petId]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleSeizureClick = (seizure) => {
    setSelectedSeizure(seizure);
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedSeizure(null);
  };

  const handlePlayVideo = async (videoUri) => {
    try {
      setVideoLoading(true);
      setVideoError(null);
      
      // Rebuild the URL with the current user's token
      const processedUrl = rebuildSingleUrlWithCurrentToken(videoUri);
      setVideoUrl(processedUrl);
      setVideoPlayerOpen(true);
    } catch (error) {
      setVideoError(t('seizures.failedToLoadVideo'));
      console.error('Error loading video:', error);
    } finally {
      setVideoLoading(false);
    }
  };

  const handleCloseVideoPlayer = () => {
    setVideoPlayerOpen(false);
    setVideoUrl(null);
    setVideoError(null);
  };

  const seizures = useMemo(() => seizuresQuery.data || [], [seizuresQuery.data]);
  const filteredSeizures = useMemo(() => seizures || [], [seizures]);

  const tooltipStyle = {
    backgroundColor: AppStyles.colors.background.paper,
    border: `1px solid ${AppStyles.colors.border.light}`,
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    padding: '8px 10px',
    minWidth: 220,
    maxWidth: 360,
    whiteSpace: 'normal',
  };

  // Multi-line, non-truncating X-axis tick renderer
  const AxisLabelTick = ({ x, y, payload, maxLineLength = 18, lines = 2 }) => {
    const raw = String(payload?.value ?? '');
    const words = raw.split(' ').filter(Boolean);
    const rows = [];
    let current = '';
    words.forEach((w) => {
      const candidate = current ? current + ' ' + w : w;
      if (candidate.length <= maxLineLength) {
        current = candidate;
      } else {
        if (current) rows.push(current);
        current = w;
      }
    });
    if (current) rows.push(current);
    const used = rows.slice(0, lines);
    return (
      <g transform={`translate(${x},${y})`}>
        {used.map((text, idx) => (
          <text key={idx} x={0} y={0} dy={14 + idx * 14} textAnchor="middle" fill={AppStyles.colors.text.secondary} fontSize={12}>
            {text}
          </text>
        ))}
      </g>
    );
  };

  const formatDuration = (s) => {
    // Backend fields may vary; cover all known variants
    const minutes = s.lengthValue ?? s.durationMinutes ?? s.durationInMinutes ?? s.duration;
    if (minutes == null || minutes === '') return '-';
    const num = typeof minutes === 'number' ? minutes : parseFloat(String(minutes));
    if (Number.isNaN(num)) return String(minutes);
    return `${num} min`;
  };

  const formatCluster = (s) => {
    const cluster = s.clusterEpisode ?? s.cluster ?? s.isCluster ?? s.clustered;
    if (cluster == null) return '-';
    return cluster ? t('seizures.yes') : t('seizures.no');
  };

  // Render helper for optional fields in the modal cards
  const renderOrNotSpecified = (val) => {
    const has = val !== null && val !== undefined && String(val).trim() !== '';
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'medium', 
          fontStyle: has ? 'normal' : 'italic', 
          color: has ? undefined : AppStyles.colors.text.secondary 
        }}
      >
        {has ? val : t('seizures.notSpecified')}
      </Typography>
    );
  };

  // Compute timeline stats
  const seizureDates = (seizures || [])
    .map(s => new Date(s.date))
    .filter(d => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b);
  const firstSeizureYear = seizureDates.length ? String(seizureDates[0].getFullYear()) : '-';
  // Use moment diff in whole days without rounding up, and clamp to 0
  const lastSeizureDaysAgo = seizureDates.length
    ? Math.max(0, moment().startOf('day').diff(moment(seizureDates[seizureDates.length - 1]).startOf('day'), 'days'))
    : 0;
  // Count seizures in the last 6 months
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const lastSixMonthsCount = (seizures || []).filter(s => {
    const d = new Date(s.date);
    return !Number.isNaN(d.getTime()) && d > sixMonthsAgo;
  }).length;
  // Average duration across all seizures (rounded to nearest integer). Ignore logs without duration
  const durationValues = (seizures || [])
    .map(s => s.lengthValue ?? s.durationMinutes ?? s.durationInMinutes ?? s.duration)
    .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
    .map(v => parseFloat(v))
    .filter(n => Number.isFinite(n));
  const avgDurationRounded = durationValues.length
    ? Math.round(durationValues.reduce((a, b) => a + b, 0) / durationValues.length)
    : null;

  // ---------- Analytics computations ----------
  const analytics = useMemo(() => {
    const list = Array.isArray(filteredSeizures) ? filteredSeizures : [];

    // Type distribution
    const typeMap = new Map();
    list.forEach((s) => {
      const seizureType = (s.type || s.seizureType || t('seizures.unknown')).trim();
      typeMap.set(seizureType, (typeMap.get(seizureType) || 0) + 1);
    });
    const typeDistribution = Array.from(typeMap.entries()).map(([type, count]) => ({ type, count }));

    // Trigger distribution (exclude empty/undefined triggers)
    const trigMap = new Map();
    list.forEach((s) => {
      const trig = ((s && s.trigger) ?? '').toString().trim();
      if (!trig) return; // skip if no trigger value
      trigMap.set(trig, (trigMap.get(trig) || 0) + 1);
    });
    const triggerDistribution = Array.from(trigMap.entries()).map(([trigger, count]) => ({ trigger, count }));

    // Cluster vs non-cluster counts
    let clusterYes = 0, clusterNo = 0;
    list.forEach((s) => {
      const c = s.clusterEpisode ?? s.cluster ?? s.isCluster ?? s.clustered;
      if (c === true) clusterYes += 1; else clusterNo += 1;
    });
    const clusterBreakdown = [
      { label: t('seizures.cluster'), count: clusterYes },
      { label: t('seizures.nonCluster'), count: clusterNo },
    ];

    // Monthly trend: from first seizure month to last seizure month
    const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = (d) => moment(d).format('MMM YYYY');
    const byMonth = new Map();
    list.forEach((s) => {
      const d = new Date(s.date);
      if (Number.isNaN(d.getTime())) return;
      const k = monthKey(new Date(d.getFullYear(), d.getMonth(), 1));
      if (!byMonth.has(k)) byMonth.set(k, []);
      byMonth.get(k).push(s);
    });
    const keysSorted = Array.from(byMonth.keys()).sort();
    let months = [];
    if (keysSorted.length > 0) {
      const first = keysSorted[0].split('-');
      const last = keysSorted[keysSorted.length - 1].split('-');
      const start = new Date(parseInt(first[0], 10), parseInt(first[1], 10) - 1, 1);
      const end = new Date(parseInt(last[0], 10), parseInt(last[1], 10) - 1, 1);
      const cursor = new Date(start);
      while (cursor <= end) {
        const k = monthKey(cursor);
        months.push({ key: k, label: monthLabel(new Date(cursor)), items: byMonth.get(k) || [] });
        cursor.setMonth(cursor.getMonth() + 1);
      }
    }
    const monthlyTrends = months.map((m) => {
      const durations = m.items
        .map((s) => s.lengthValue ?? s.durationMinutes ?? s.durationInMinutes ?? s.duration)
        .map((v) => parseFloat(v))
        .filter((n) => Number.isFinite(n));
      if (!durations.length) {
        return { month: m.label, count: m.items.length, avgDuration: null };
      }
      const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
      return { month: m.label, count: m.items.length, avgDuration: Math.round(avg * 10) / 10 };
    });

    return { typeDistribution, triggerDistribution, clusterBreakdown, monthlyTrends };
  }, [filteredSeizures, t]);

  // Set default pagination window to show the group that contains the last seizure
  useEffect(() => {
    const total = analytics?.monthlyTrends?.length || 0;
    if (total === 0) {
      setTrendStart(0);
      return;
    }
    const lastIndex = total - 1; // last month in the series corresponds to last seizure month
    const start = Math.max(0, lastIndex - 5);
    setTrendStart(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics?.monthlyTrends?.length]);

  // Navigation helpers for detail dialog
  const currentIndex = selectedSeizure
    ? filteredSeizures.findIndex((s) => (s.id || s._id) === (selectedSeizure.id || selectedSeizure._id))
    : -1;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex > -1 && currentIndex < filteredSeizures.length - 1;

  const goPrev = useCallback(() => {
    if (canPrev) {
      setSelectedSeizure(filteredSeizures[currentIndex - 1]);
    }
  }, [canPrev, currentIndex, filteredSeizures]);

  const goNext = useCallback(() => {
    if (canNext) {
      setSelectedSeizure(filteredSeizures[currentIndex + 1]);
    }
  }, [canNext, currentIndex, filteredSeizures]);

  useEffect(() => {
    if (!detailDialogOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [detailDialogOpen, goPrev, goNext]);

  // Early exits: show only one thing based on state
  if (seizuresQuery.isLoading) {
    return <CardSkeleton count={3} />;
  }
  if (seizuresQuery.isError) {
    return <ErrorState />;
  }
  if (!seizures || seizures.length === 0) {
    return (
      <NoEntries
        title={t('common.noSeizuresTitle')}
        description={t('common.noSeizuresDesc')}
        icon="seizures"
      />
    );
  }

  return (
    <Box>

      {/* Summary Statistics */}
      {(
      <Paper 
        elevation={8}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: 'transparent',
          border: `1px solid ${AppStyles.colors.border.light}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
          }
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.semibold }}>
          {t('seizures.summaryStatistics')}
        </Typography>
        <Grid container spacing={3} justifyContent="space-around" alignItems="stretch">
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <CalendarIcon sx={{ fontSize: 40, mb: 1, color: AppStyles.colors.error.light, opacity: 0.85 }} />
              <Typography variant="h4">{lastSeizureDaysAgo}</Typography>
              <Typography variant="body2" color="text.secondary">{t('seizures.daysSince')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('seizures.lastSeizure')}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <WarningIcon sx={{ fontSize: 40, mb: 1, color: AppStyles.colors.warning.light, opacity: 0.85 }} />
              <Typography variant="h4">{lastSixMonthsCount}</Typography>
              <Typography variant="body2" color="text.secondary">{t('seizures.totalSeizures')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('seizures.lastSixMonths')}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <TimeIcon sx={{ fontSize: 40, mb: 1, color: AppStyles.colors.primary.light, opacity: 0.85 }} />
              <Typography variant="h4">{avgDurationRounded == null ? '-' : avgDurationRounded}</Typography>
              <Typography variant="body2" color="text.secondary">{t('seizures.averageDuration')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('seizures.minutes')}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <FirstSeizureIcon sx={{ fontSize: 40, mb: 1, color: AppStyles.colors.success.light, opacity: 0.85 }} />
              <Typography variant="h4">{firstSeizureYear}</Typography>
              <Typography variant="body2" color="text.secondary">{t('seizures.diagnosed')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('seizures.firstSeizure')}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Tabs */}
      {(
        <Paper 
          sx={{ 
            mb: 3,
            p: 1,
            borderRadius: 3,
            background: 'transparent',
            border: `1px solid ${AppStyles.colors.border.light}`,
            boxShadow: AppStyles.shadows.sm,
          }}
        >
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': { gap: 8 },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab 
              label={t('seizures.seizureLog')} 
              sx={{
                minHeight: 40,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                color: AppStyles.colors.text.secondary,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                fontWeight: AppStyles.typography.fontWeight.medium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
                },
                '&.Mui-selected': {
                  color: AppStyles.colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  boxShadow: AppStyles.shadows.sm,
                },
              }}
            />
            <Tab 
              label={t('seizures.analytics')} 
              sx={{
                minHeight: 40,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                color: AppStyles.colors.text.secondary,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                fontWeight: AppStyles.typography.fontWeight.medium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
                },
                '&.Mui-selected': {
                  color: AppStyles.colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  boxShadow: AppStyles.shadows.sm,
                },
              }}
            />
            <Tab 
              label={t('seizures.trends')} 
              sx={{
                minHeight: 40,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                color: AppStyles.colors.text.secondary,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                fontWeight: AppStyles.typography.fontWeight.medium,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}30 0%, ${AppStyles.colors.secondary.main}30 100%)`,
                },
                '&.Mui-selected': {
                  color: AppStyles.colors.primary.contrastText,
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  boxShadow: AppStyles.shadows.sm,
                },
              }}
            />
          </Tabs>
        </Paper>
      )}

  {/* Seizure Log Tab */}
  {false && <CardSkeleton count={3} />}
  {false && <ErrorState />}
  {false && seizures.length === 0 && (
    <NoEntries
      title={t('common.noSeizuresTitle')}
      description={t('common.noSeizuresDesc')}
      icon="seizures"
    />
  )}
  {seizures.length > 0 && selectedTab === 0 && (
        <Grid container spacing={3} sx={{ width: '100%', mx: 0 }}>
          <Grid item xs={12} sx={{ width: '100%' }}>
            <Paper 
              elevation={8}
              sx={{ 
                p: 2,
                borderRadius: 3,
                background: 'transparent',
                border: `1px solid ${AppStyles.colors.border.light}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
                },
                width: '100%',
                mx: 0,
              }}
            >
              <TableContainer sx={{ width: '100%' }}>
                <Table sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium } }}>
                      <TableCell>{t('seizures.dateTime')}</TableCell>
                      <TableCell>{t('seizures.type')}</TableCell>
                      <TableCell>{t('seizures.duration')}</TableCell>
                      <TableCell>{t('seizures.cluster')}</TableCell>
                      <TableCell>{t('seizures.video')}</TableCell>
                      <TableCell align="right" sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSeizures.map((s) => (
                      <TableRow 
                        key={s.id || s._id} 
                        hover 
                        onClick={() => handleSeizureClick(s)}
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          '& td': {
                            borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                          },
                          '&:hover': {
                            background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}0A 0%, ${AppStyles.colors.secondary.main}0A 100%)`,
                            boxShadow: '0 8px 20px rgba(0,0,0,0.06), inset 4px 0 0 0 ' + AppStyles.colors.secondary.main,
                            transform: 'translateY(-1px)'
                          },
                          '&:hover .row-chevron': {
                            opacity: 1,
                            color: AppStyles.colors.secondary.main,
                            transform: 'translateX(2px)'
                          }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {s.date ? moment(s.date).format('MMM DD, YYYY') : '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {s.time || moment(s.date).format('HH:mm')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={s.type || s.seizureType || '-'} 
                            size="small"
                            sx={{
                              borderRadius: 999,
                              px: 1,
                              height: 24,
                              background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}26 0%, ${AppStyles.colors.primary.main}1A 35%, ${AppStyles.colors.secondary.main}40 100%)`,
                              border: `1px solid ${AppStyles.colors.border.light}`,
                              color: AppStyles.colors.text.primary,
                              fontWeight: AppStyles.typography.fontWeight.medium,
                              boxShadow: AppStyles.shadows.sm,
                              textTransform: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDuration(s)}</TableCell>
                        <TableCell>{formatCluster(s)}</TableCell>
                        <TableCell>
                          {s.videoUri ? (
                            <VideoIcon sx={{ color: AppStyles.colors.secondary.light, fontSize: 24 }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 48, pr: 1 }}>
                          <ChevronRightIcon className="row-chevron" sx={{ color: AppStyles.colors.text.secondary, opacity: 0.4, transition: 'opacity .2s, transform .2s, color .2s' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Analytics Tab */}
      {selectedTab === 1 && (
        <Grid container spacing={3} direction="column" wrap="nowrap">
          {/* Seizure Type Distribution */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 300, md: 380 }, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden' }}>
              <Typography variant="h6" gutterBottom>
                {t('seizures.seizureTypes')}
              </Typography>
              {analytics.typeDistribution.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.typeDistribution} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                    <XAxis dataKey="type" interval={0} height={40} tickMargin={10} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} tick={(props) => <AxisLabelTick {...props} />} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} />
                    <Tooltip wrapperStyle={tooltipStyle} cursor={{ fill: AppStyles.colors.primary.main + '10' }} formatter={(value, name) => [value, name]} labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload ? (payload[0].payload.type || label) : label)} />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} fill={`url(#typeGradient)`} />
                    <defs>
                      <linearGradient id="typeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#A3B18A" />
                        <stop offset="100%" stopColor="#FFB74D" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>{t('seizures.noData')}</Box>
              )}
            </Paper>
          </Grid>

          {/* Trigger Analysis - render only if there are triggers */}
          {analytics.triggerDistribution.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 300, md: 380 }, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden' }}>
                <Typography variant="h6" gutterBottom>
                  {t('seizures.commonTriggers')}
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.triggerDistribution} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                    <XAxis dataKey="trigger" interval={0} height={40} tickMargin={10} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} tick={(props) => <AxisLabelTick {...props} />} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} />
                    <Tooltip wrapperStyle={tooltipStyle} cursor={{ fill: AppStyles.colors.secondary.main + '10' }} formatter={(value, name) => [value, name]} labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload ? (payload[0].payload.trigger || label) : label)} />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} fill={`url(#triggerGradient)`} />
                    <defs>
                      <linearGradient id="triggerGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={AppStyles.colors.error.main} />
                        <stop offset="100%" stopColor={AppStyles.colors.warning.main} />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* Cluster Breakdown */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 300, md: 380 }, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden' }}>
              <Typography variant="h6" gutterBottom>
                {t('seizures.clusterVsNonCluster')}
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.clusterBreakdown} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                  <XAxis dataKey="label" interval={0} height={40} tickMargin={10} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} tick={(props) => <AxisLabelTick {...props} />} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} />
                  <Tooltip wrapperStyle={tooltipStyle} cursor={{ fill: AppStyles.colors.info.main + '10' }} />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]} fill={`url(#clusterGradient)`} />
                  <defs>
                    <linearGradient id="clusterGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AppStyles.colors.info.main} />
                      <stop offset="100%" stopColor={AppStyles.colors.success.main} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Trends Tab */}
      {selectedTab === 2 && (
        <Grid container spacing={3} direction="column" wrap="nowrap">
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, md: 460 }, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, position: 'relative' }}>
              <Typography variant="h6" gutterBottom>
                {t('seizures.monthlySeizureTrends')}
              </Typography>
              {analytics.monthlyTrends.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.monthlyTrends.slice(trendStart, trendStart + 6)} margin={{ top: 10, right: 80, left: 40, bottom: 30 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                    <XAxis
                      dataKey="month"
                      interval={0}
                      height={60}
                      tickMargin={20}
                      tickLine={false}
                      axisLine={{ stroke: AppStyles.colors.border.light }}
                      tick={<AxisLabelTick maxLineLength={12} lines={1} />}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={{ stroke: AppStyles.colors.border.light }} />
                    <Tooltip wrapperStyle={tooltipStyle} formatter={(value) => [value, t('seizures.seizureCount')]} />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke={AppStyles.colors.primary.main}
                      strokeWidth={3}
                      dot={(p) => (p.payload && p.payload.count > 0 ? (
                        <circle cx={p.cx} cy={p.cy} r={3} fill={AppStyles.colors.primary.main} strokeWidth={2} />
                      ) : null)}
                      name={t('seizures.seizureCount')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>{t('seizures.noData')}</Box>
              )}
              {analytics.monthlyTrends.length > 6 && (
                <>
                  <IconButton
                    onClick={() => setTrendStart((s) => Math.max(0, s - 6))}
                    disabled={trendStart === 0}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: AppStyles.colors.background.paper,
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      boxShadow: AppStyles.shadows.sm,
                      '&:hover': { backgroundColor: AppStyles.colors.background.default },
                    }}
                    aria-label={t('seizures.previousMonths')}
                  >
                    <PrevIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setTrendStart((s) => Math.min(analytics.monthlyTrends.length - 6, s + 6))}
                    disabled={trendStart + 6 >= analytics.monthlyTrends.length}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: AppStyles.colors.background.paper,
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      boxShadow: AppStyles.shadows.sm,
                      '&:hover': { backgroundColor: AppStyles.colors.background.default },
                    }}
                    aria-label={t('seizures.nextMonths')}
                  >
                    <NextIcon />
                  </IconButton>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Seizure Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: AppStyles.colors.background.paper,
            border: `1px solid ${AppStyles.colors.border.light}`,
          }
        }}
      >
        {selectedSeizure && (
          <>
            <DialogTitle sx={{ 
              background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
              color: '#fff',
              borderRadius: '12px 12px 0 0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <IconButton onClick={goPrev} disabled={!canPrev} sx={{ color: AppStyles.colors.primary.contrastText }}>
                  <PrevIcon />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Notes />
                  {t('seizures.seizureDetails')}{moment(selectedSeizure.date).format('MMM DD, YYYY')} | {(selectedSeizure.time || selectedSeizure.date) ? ` ${selectedSeizure.time || moment(selectedSeizure.date).format('HH:mm')}` : ''}
                </Box>
                <IconButton onClick={goNext} disabled={!canNext} sx={{ color: AppStyles.colors.primary.contrastText }}>
                  <NextIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* Header Summary Card */}
              <Box sx={{ 
                p: 3, 
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}10 0%, ${AppStyles.colors.secondary.main}10 100%)`,
                borderBottom: `1px solid ${AppStyles.colors.border.light}`
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap'
                  }}>
                    {selectedSeizure.type && (
                      <Chip 
                        label={selectedSeizure.type}
                        size="small"
                        sx={{ 
                          background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}20 0%, ${AppStyles.colors.secondary.main}20 100%)`,
                          color: AppStyles.colors.primary.dark,
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 26,
                        }} 
                      />
                    )}
                    {formatDuration(selectedSeizure) !== '-' && (
                      <Chip 
                        label={formatDuration(selectedSeizure)} 
                        size="small"
                        sx={{ 
                          background: `linear-gradient(135deg, ${AppStyles.colors.warning.main}20 0%, ${AppStyles.colors.error.main}20 100%)`,
                          color: AppStyles.colors.warning.dark,
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 26,
                        }} 
                      />
                    )}
                    {formatCluster(selectedSeizure) === t('seizures.yes') && (
                      <Chip 
                        label={t('seizures.cluster')} 
                        size="small"
                        sx={{ 
                          background: `linear-gradient(135deg, ${AppStyles.colors.error.main}20 0%, ${AppStyles.colors.warning.main}20 100%)`,
                          color: AppStyles.colors.error.dark,
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 26,
                        }} 
                      />
                    )}
                    {selectedSeizure.emergencyMedication && (
                      <Chip 
                        label={selectedSeizure.emergencyMedication} 
                        size="small"
                        sx={{ 
                          background: `linear-gradient(135deg, ${AppStyles.colors.success.main}20 0%, ${AppStyles.colors.info.main}20 100%)`,
                          color: AppStyles.colors.success.dark,
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          height: 26,
                        }} 
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Main Content */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3} justifyContent="center">
                  {/* Left Column - Basic Information */}
                  <Grid item xs={12} sm={10} md={3}>
                    <Paper sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      background: AppStyles.colors.background.paper,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
                        color: AppStyles.colors.primary.dark,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <BasicInfoIcon sx={{ fontSize: 20 }} />
                        {t('seizures.basicInformation')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.state')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.state)}
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.trigger')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.trigger)}
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.mealTimeRelation')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.mealTimeRelation)}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Middle Column - Behavioral Details */}
                  <Grid item xs={12} sm={10} md={3}>
                    <Paper sx={{ 
                      p: 2.5, 
                      borderRadius: 2, 
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      background: AppStyles.colors.background.paper,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
                        color: AppStyles.colors.primary.dark,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <BehaviorInfoIcon sx={{ fontSize: 20 }} />
                        {t('seizures.behavioralDetails')}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.preSeizureBehavior')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.preSeizureBehavior)}
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.duringSeizureBehavior')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.duringSeizureBehavior)}
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" sx={{ color: AppStyles.colors.text.secondary, fontWeight: 'bold' }}>
                            {t('seizures.postSeizureBehavior')}
                          </Typography>
                          {renderOrNotSpecified(selectedSeizure.postSeizureBehavior)}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Right Column - Video Recording */}
                  {selectedSeizure.videoUri && (
                    <Grid item xs={12} sm={10} md={3}>
                      <Paper sx={{ 
                        p: 2.5, 
                        borderRadius: 2, 
                        border: `1px solid ${AppStyles.colors.border.light}`,
                        background: AppStyles.colors.background.paper,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
                          color: AppStyles.colors.primary.dark,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          <VideoIcon sx={{ fontSize: 20 }} />
                          {t('seizures.videoRecording')}
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center',
                          alignItems: 'center',
                          flex: 1
                        }}>
                          <Button
                            variant="contained"
                            startIcon={videoLoading ? <CircularProgress size={20} /> : <PlayIcon />}
                            onClick={() => handlePlayVideo(selectedSeizure.videoUri)}
                            disabled={videoLoading}
                            sx={{
                              background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                              color: AppStyles.colors.primary.contrastText,
                              borderRadius: 2,
                              px: 3,
                              py: 1.5,
                              textTransform: 'none',
                              fontWeight: AppStyles.typography.fontWeight.medium,
                              fontSize: '1rem',
                              '&:hover': {
                                background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark} 0%, ${AppStyles.colors.secondary.dark} 100%)`,
                              },
                              '&:disabled': {
                                background: AppStyles.colors.neutral.gray300,
                                color: AppStyles.colors.text.disabled,
                              }
                            }}
                          >
                            {videoLoading ? t('seizures.loading') : t('seizures.playVideo')}
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={handleCloseDialog}
                variant="contained"
                sx={{
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
                  color: '#fff',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: AppStyles.typography.fontWeight.medium,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
                  }
                }}
              >
                {t('seizures.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog 
        open={videoPlayerOpen} 
        onClose={handleCloseVideoPlayer}
        maxWidth={false}
        fullWidth
        fullScreen={false}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: AppStyles.colors.background.paper,
            border: `1px solid ${AppStyles.colors.border.light}`,
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark || AppStyles.colors.primary.main}, ${AppStyles.colors.primary.main} 65%, ${AppStyles.colors.secondary.main})`,
          color: '#fff',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoIcon />
            {t('seizures.seizureVideoRecording')}
          </Box>
          <IconButton 
            onClick={handleCloseVideoPlayer}
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', height: 'calc(100% - 64px)', overflow: 'hidden' }}>
          {videoError ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {videoError}
              </Alert>
              <Button 
                onClick={handleCloseVideoPlayer}
                variant="contained"
                sx={{
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  color: AppStyles.colors.primary.contrastText,
                }}
              >
                {t('seizures.close')}
              </Button>
            </Box>
          ) : videoUrl ? (
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: '100%',
              backgroundColor: '#000',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <video
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                  onError={() => setVideoError(t('seizures.failedToLoadVideoFormat'))}
                >
                  <source src={videoUrl} type="video/mp4" />
                  <source src={videoUrl} type="video/quicktime" />
                  <source src={videoUrl} type="video/webm" />
                  {t('seizures.videoNotSupported')}
                </video>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t('seizures.loadingVideo')}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Seizures;
